/**
 * Year6ParentViewPage — /year6/:slug/view
 *
 * The conversion engine for parents. Hero + live photo grid → highlight
 * video autoplay → scrolling memory messages → paywall transition with
 * progressive blur → premium £99 unlock card.
 *
 * Psychology stack: Recognition → Emotion → Loss aversion → Simplicity.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCap, LockKey, Check, Sparkle, Heart, Image as ImgIcon, ChatCircleText, Play, ArrowDown, BookOpen, MagnifyingGlass, X, Eye, DownloadSimple } from '@phosphor-icons/react';
import Year6BookPreviewModal from '../components/Year6BookPreviewModal';
import { AlmostReadyBoost } from '../components/Year6LowContentRecovery';
import { NewContentBanner, ParentSubscribeCard, UpdatedSinceLastDownloadBadge, stampDownloadedVersion } from '../components/Year6NewContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 1 session = 1 view. Stable per-browser anon_id.
function getAnonId() {
  try {
    let id = localStorage.getItem('anon_id');
    if (!id) {
      id = `anon_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
      localStorage.setItem('anon_id', id);
    }
    return id;
  } catch { return null; }
}

// Round views: 1-5 exact, 6-20 → "10+", 20+ → "20+" (no inflation, just bracketing)
function _roundViews(n) {
  if (n <= 5) return String(n);
  if (n < 20) return '10+';
  if (n < 50) return '20+';
  if (n < 100) return '50+';
  return '100+';
}
function _roundDownloads(n) {
  if (n <= 5) return String(n);
  if (n < 20) return '10+';
  if (n < 50) return '20+';
  return '50+';
}

export default function Year6ParentViewPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [eventId, setEventId] = useState(null);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);   // {student_id, name}
  const [childPickerOpen, setChildPickerOpen] = useState(false);
  const [childQuery, setChildQuery] = useState('');
  const didHydrateChild = useRef(false); // run URL/localStorage hydration once

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const r = await axios.get(`${API}/events/slug/${slug}`); if (!cancelled) setEventId(r.data.event_id); }
      catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const anon = getAnonId();
        const url = `${API}/events/${eventId}/year6/parent-view-data${anon ? `?anon_id=${encodeURIComponent(anon)}` : ''}`;
        const [r, s] = await Promise.all([
          axios.get(url),
          axios.get(`${API}/events/${eventId}/year6/stats`).catch(() => null),
        ]);
        if (cancelled) return;
        setData(r.data);
        if (s?.data) setStats(s.data);
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 30000); // refresh every 30s for live feel
    return () => { cancelled = true; clearInterval(t); };
  }, [eventId]);

  // Per-child ownership: hydrate selected child from URL (?child=Name) or localStorage.
  // Runs ONCE per page load when roster first arrives — so an explicit clear
  // via persistChild(null) doesn't get reverted by the URL param re-matching.
  useEffect(() => {
    if (didHydrateChild.current) return;
    if (!data?.roster) return;
    const roster = data.roster || [];
    if (roster.length === 0) return;
    didHydrateChild.current = true;
    const fromUrl = (searchParams.get('child') || '').trim().toLowerCase();
    if (fromUrl) {
      const m = roster.find((s) => (s.name || '').toLowerCase() === fromUrl ||
                                   (s.student_id || '') === fromUrl);
      if (m) {
        setSelectedChild(m);
        try { localStorage.setItem(`year6_child_${slug}`, m.student_id); } catch { /* ignore */ }
        return;
      }
    }
    try {
      const stored = localStorage.getItem(`year6_child_${slug}`);
      if (stored) {
        const m = roster.find((s) => s.student_id === stored);
        if (m) setSelectedChild(m);
      }
    } catch { /* ignore */ }
  }, [data?.roster, searchParams, slug]);

  const persistChild = (child) => {
    setSelectedChild(child);
    try {
      if (child) localStorage.setItem(`year6_child_${slug}`, child.student_id);
      else localStorage.removeItem(`year6_child_${slug}`);
    } catch { /* ignore */ }
    setChildPickerOpen(false);
    setChildQuery('');
    if (child) trackChildSelected(child);
  };

  const filteredRoster = useMemo(() => {
    const q = childQuery.trim().toLowerCase();
    const r = data?.roster || [];
    if (!q) return r.slice(0, 12);
    return r.filter((s) => (s.name || '').toLowerCase().includes(q)).slice(0, 12);
  }, [childQuery, data?.roster]);

  // Funnel: track ?src=email click-through ONCE per page load
  useEffect(() => {
    if (!eventId) return;
    if (searchParams.get('src') !== 'email') return;
    let fired = false;
    if (fired) return;
    fired = true;
    axios.post(`${API}/events/${eventId}/year6/track`, {
      event: 'year6_email_link_clicked',
      metadata: { anon_id: getAnonId() },
    }).catch(() => { /* fire-and-forget */ });
  }, [eventId, searchParams]);

  // Funnel: track child-selection (only when user picks via UI, not URL hydration)
  const trackChildSelected = (child) => {
    if (!eventId || !child) return;
    axios.post(`${API}/events/${eventId}/year6/track`, {
      event: 'year6_child_selected',
      metadata: { student_id: child.student_id, anon_id: getAnonId() },
    }).catch(() => { /* fire-and-forget */ });
  };

  // Personalised share — copy adapts to selectedChild for parent-side sharing
  const buildShareMessage = () => {
    const url = typeof window !== 'undefined'
      ? `${window.location.origin}/year6/${slug}/view`
      : '';
    if (selectedChild) {
      const fn = (selectedChild.name || '').split(' ')[0];
      return `\u{1F393} ${fn}'s Year 6 Memory Book is ready!\n\nWe've created something really special with photos, messages and a personalised certificate.\n\nTake a look \u{1F447}\n${url}`;
    }
    return `\u{1F393} Our Year 6 Memory Book is ready!\n\nPhotos, messages and a personalised certificate for every child.\n\nTake a look \u{1F447}\n${url}`;
  };
  const trackShareClick = (channel) => {
    if (!eventId) return;
    axios.post(`${API}/events/${eventId}/year6/track`, {
      event: 'year6_share_clicked',
      metadata: { channel, child_selected: !!selectedChild, anon_id: getAnonId() },
    }).catch(() => { /* fire-and-forget */ });
  };
  const handleShareWhatsApp = () => {
    trackShareClick('whatsapp');
    window.open(`https://wa.me/?text=${encodeURIComponent(buildShareMessage())}`, '_blank');
  };
  const handleShareCopy = async () => {
    trackShareClick('copy');
    try {
      const url = `${window.location.origin}/year6/${slug}/view`;
      await navigator.clipboard.writeText(url);
      toast.success('Memory Book link copied');
    } catch { toast.error('Could not copy'); }
  };
  const handleShareEmail = () => {
    trackShareClick('email');
    const subject = selectedChild
      ? `${(selectedChild.name || '').split(' ')[0]}'s Year 6 Memory Book`
      : 'Our Year 6 Memory Book is ready 🎓';
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildShareMessage())}`;
  };

  // Slow photo rotation in the hero grid for the "live, real" feeling
  useEffect(() => {
    const photoCount = data?.photo_urls?.length || 0;
    if (photoCount < 2) return;
    const t = setInterval(() => setActivePhotoIdx((i) => (i + 1) % photoCount), 2800);
    return () => clearInterval(t);
  }, [data?.photo_urls?.length]);

  const handleUnlock = async () => {
    if (!eventId) return;
    setCheckingOut(true);
    // Mini-loader UX detail per spec — 350ms perceived smoothness
    setTimeout(async () => {
      try {
        let anonId = null;
        try { anonId = localStorage.getItem('anon_id'); } catch { /* */ }
        const r = await axios.post(`${API}/events/${eventId}/year6/memory-book/checkout`, {
          anon_id: anonId, origin: window.location.origin,
        });
        if (r.data?.url) window.location.href = r.data.url;
        else { toast.error('Could not start checkout'); setCheckingOut(false); }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not start checkout');
        setCheckingOut(false);
      }
    }, 350);
  };

  const heroPhotos = (data?.photo_urls || []).slice(0, 12);
  const messages = data?.messages || [];
  const highlightUrl = data?.highlight_url
    ? (data.highlight_url.startsWith('/api') ? `${BACKEND_URL}${data.highlight_url}` : data.highlight_url)
    : null;

  return (
    <div className="min-h-screen bg-neutral-950 text-white overflow-x-hidden" data-testid="year6-parent-view-page">
      {/* Re-entry banner — fires once per session per slug if content has grown since last visit */}
      <NewContentBanner slug={slug} stats={stats} purchased={!!data?.memory_book_purchased} />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex flex-col items-center justify-center px-5 py-16 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 30% 20%, rgba(59,130,246,0.30), transparent 45%), radial-gradient(ellipse at 80% 80%, rgba(250,204,21,0.18), transparent 50%)' }} />

        {/* Live preview grid — photos cycle slowly */}
        {heroPhotos.length > 0 && (
          <div className="absolute inset-0 grid grid-cols-3 sm:grid-cols-4 gap-1 opacity-25 pointer-events-none" data-testid="year6-pv-photo-grid">
            {heroPhotos.slice(0, 12).map((url, i) => (
              <motion.div
                key={url}
                initial={{ opacity: 0 }}
                animate={{ opacity: i === activePhotoIdx ? 0.65 : 0.3 }}
                transition={{ duration: 1.5 }}
                className="aspect-square overflow-hidden"
                style={{ filter: 'blur(1px)' }}
              >
                <img src={url.startsWith('http') ? url : `${BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
              </motion.div>
            ))}
          </div>
        )}
        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(10,11,26,0.7) 0%, rgba(10,11,26,0.3) 40%, rgba(10,11,26,0.95) 100%)' }} />

        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-300/15 border border-amber-300/30 text-amber-200 text-[10px] font-black uppercase tracking-[0.18em] mb-6">
            <GraduationCap size={11} weight="fill" /> 🎓 Year 6 Memories
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.02] mb-5" data-testid="year6-pv-title">
            <span style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #FACC15 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {selectedChild ? `${(selectedChild.name || '').split(' ')[0]}'s Year 6 Memories` : (data?.event_name || 'Year 6')}
            </span>
          </h1>
          {/* Hero subhead — content-state aware (very_low → reassurance, otherwise → standard) */}
          {(stats?.very_low_content && !stats?.purchased) ? (
            <div className="mb-6 max-w-md mx-auto" data-testid="year6-pv-very-low-state">
              <p className="text-white/80 text-lg leading-relaxed mb-1.5">
                This Memory Book is still being filled <span aria-hidden="true">💛</span>
              </p>
              <p className="text-white/55 text-[13.5px] leading-relaxed">
                New photos and messages are being added today.
              </p>
              <p className="text-white/45 text-[12.5px] mt-2 italic">
                Check back later today — more moments are coming.
              </p>
            </div>
          ) : (
            <p className="text-white/70 text-lg mb-3 max-w-md mx-auto leading-relaxed" data-testid="year6-pv-subhead">
              {selectedChild
                ? `See ${(selectedChild.name || '').split(' ')[0]}'s messages, photos and personalised certificate.`
                : "See what your child's class has created together."}
            </p>
          )}

          {/* Per-child selector */}
          {(data?.roster?.length || 0) > 0 && (
            <div className="flex items-center justify-center mb-6" data-testid="year6-pv-child-selector">
              {selectedChild ? (
                <div className="inline-flex items-center gap-1 pl-3.5 pr-1 py-1 rounded-full bg-white/8 hover:bg-white/14 border border-white/15 transition-colors" data-testid="year6-pv-child-pill">
                  <button
                    type="button"
                    onClick={() => setChildPickerOpen(true)}
                    className="text-white/90 text-[12px] font-bold py-1"
                    data-testid="year6-pv-child-pill-open"
                  >
                    Viewing as <span className="font-black">{selectedChild.name}</span>
                  </button>
                  <button
                    type="button"
                    aria-label="Clear child selection"
                    onClick={() => persistChild(null)}
                    className="ml-0.5 p-1.5 rounded-full hover:bg-white/15 cursor-pointer text-white/55 hover:text-white"
                    data-testid="year6-pv-child-pill-clear"
                  >
                    <X size={13} weight="bold" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setChildPickerOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-amber-300/12 hover:bg-amber-300/20 border border-amber-300/35 text-amber-200 text-[12px] font-bold transition-colors"
                  data-testid="year6-pv-child-prompt"
                >
                  <MagnifyingGlass size={12} weight="bold" /> Who is your child?
                </button>
              )}
            </div>
          )}

          {/* Quick stats — proves activity */}
          <div className="flex justify-center gap-8 mb-4 text-center" data-testid="year6-pv-stats">
            <Stat n={data?.total_photos} label="photos" />
            <Stat n={data?.total_messages} label="messages" />
            <Stat n={data?.total_videos} label="videos" />
          </div>
          {/* "More memories being added" — low-content reassurance (not very_low, not purchased) */}
          {stats && !stats.purchased && stats.low_content && !stats.very_low_content && (
            <p
              className="text-white/65 text-[12.5px] mb-3 inline-flex items-center gap-1.5"
              data-testid="year6-pv-more-coming-pill"
            >
              <Sparkle size={11} weight="fill" className="text-amber-300" />
              More memories are being added today
            </p>
          )}
          {/* Hero social proof — only show when 3+ real views */}
          {(data?.parent_views || 0) >= 3 && (
            <p className="text-white/55 text-[12px] mb-6 inline-flex items-center gap-1.5" data-testid="year6-pv-views-pill">
              <Eye size={11} weight="fill" className="text-emerald-300" />
              {_roundViews(data.parent_views)} parents have already viewed this
            </p>
          )}
          <div className="text-white/45 text-xs inline-flex items-center gap-1.5">
            <ArrowDown size={12} weight="bold" className="animate-bounce" /> Scroll to see more
          </div>
        </div>
      </section>

      {/* REAL PHOTO STRIP — visible, identifiable, "this is real" emotional proof */}
      {heroPhotos.length > 0 && (
        <section id="year6-pv-photos" className="px-5 py-10" data-testid="year6-pv-photo-strip">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-5">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-400/10 border border-blue-400/25 text-blue-300 text-[10px] font-black uppercase tracking-[0.18em] mb-3">
                📸 From your child's class
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-black">Real moments, captured today</h2>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2">
              {heroPhotos.slice(0, 6).map((url, i) => (
                <motion.div
                  key={url}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: i * 0.07, duration: 0.5 }}
                  className="aspect-square overflow-hidden rounded-xl border border-white/10 shadow-lg"
                  data-testid={`year6-pv-photo-strip-${i}`}
                >
                  <img src={url.startsWith('http') ? url : `${BACKEND_URL}${url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                </motion.div>
              ))}
            </div>
            {(data?.total_photos || 0) > 6 && (
              <p className="text-center text-white/40 text-xs mt-4">
                + {Math.max(0, (data?.total_photos || 0) - 6)} more in the full Memory Book
              </p>
            )}
          </div>
        </section>
      )}

      {/* HIGHLIGHT VIDEO */}
      {highlightUrl && (
        <section className="px-5 py-14" data-testid="year6-pv-highlight-section">
          <div className="max-w-md mx-auto text-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#B89762]/40/10 border border-[#B89762]/45/25 text-[#C29D52] text-[10px] font-black uppercase tracking-[0.18em] mb-4">
              🎬 Class Highlights
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-black mb-6">A taste of what they've made</h2>
            <div className="relative rounded-3xl overflow-hidden mx-auto bg-black" style={{ width: 280, height: 498 }}>
              <video
                src={highlightUrl}
                autoPlay muted loop playsInline
                controls={false}
                className="w-full h-full object-cover"
                data-testid="year6-pv-highlight-video"
              />
              <button
                onClick={(e) => { const v = e.currentTarget.previousElementSibling; if (v) { v.muted = false; v.controls = true; v.currentTime = 0; v.play(); } e.currentTarget.style.display = 'none'; }}
                className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-t from-black/60 via-transparent to-transparent text-white"
                data-testid="year6-pv-highlight-play"
              >
                <span className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center"><Play size={28} weight="fill" /></span>
                <span className="text-sm font-bold drop-shadow">Tap to watch full video</span>
              </button>
            </div>
          </div>
        </section>
      )}

      {/* MEMORY MESSAGES */}
      {messages.length > 0 && (
        <section className="px-5 py-14" data-testid="year6-pv-messages-section">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-7">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-400/10 border border-rose-400/25 text-rose-300 text-[10px] font-black uppercase tracking-[0.18em] mb-3">
                💌 Messages from the class
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-black">In their own words</h2>
            </div>
            <div className="space-y-3">
              {messages.slice(0, 5).map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-5"
                  data-testid={`year6-pv-message-${i}`}
                >
                  <Heart size={14} weight="fill" className="text-rose-300 mb-2" />
                  <p className="text-white/85 text-base leading-relaxed">"{(m.text || '').slice(0, 220)}"</p>
                  {m.author && <p className="text-white/45 text-xs mt-2">— {m.author}</p>}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PAYWALL TRANSITION → LOCK CARD */}
      <section className="relative px-5 py-16 sm:py-24" data-testid="year6-pv-paywall">
        {/* Soft fade overlay above */}
        <div className="absolute -top-32 left-0 right-0 h-32 pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(10,11,26,0.95) 100%)' }} />

        <div className="max-w-md mx-auto">
          {/* Parent email subscribe — capture intent right above the paywall.
              Visible to all (incl. post-purchase) so re-download notifications can fire. */}
          {eventId && <ParentSubscribeCard eventId={eventId} />}

          <div className="rounded-3xl border-2 border-amber-300/30 p-7 sm:p-9 shadow-2xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.10) 0%, rgba(139,92,246,0.06) 50%, rgba(250,204,21,0.04) 100%)' }} data-testid="year6-pv-lock-card">
            {/* Glow accent */}
            <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-30 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, #FACC15, transparent)' }} />

            <div className="relative">
              <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-amber-300/20 text-amber-300 mb-4">
                <LockKey size={22} weight="fill" />
              </div>
              {/* Social proof inside paywall — only shown when at least 1 real download AND
                  the viewing parent has not yet unlocked (otherwise it's redundant) */}
              {(data?.parent_downloads || 0) >= 1 && !data?.memory_book_purchased && (
                <p className="inline-flex items-center gap-1.5 text-[12px] font-bold text-emerald-300 mb-3" data-testid="year6-pv-downloads-pill">
                  <DownloadSimple size={12} weight="fill" />
                  {_roundDownloads(data.parent_downloads)} parent{(data.parent_downloads === 1) ? ' has' : 's have'} already downloaded their copy
                </p>
              )}
              <h2 className="font-serif text-3xl sm:text-4xl font-black leading-tight mb-3" data-testid="year6-pv-paywall-title">
                {selectedChild
                  ? <>🎉 Unlock {(selectedChild.name || '').split(' ')[0]}'s<br/>Year 6 Memory Book</>
                  : <>🎉 Your child's Year 6<br/>Memory Book is ready</>}
              </h2>
              <p className="text-white/65 text-base mb-5" data-testid="year6-pv-paywall-sub">
                {selectedChild
                  ? <>Includes {(selectedChild.name || '').split(' ')[0]}'s photos, messages and personalised certificate.</>
                  : 'This Memory Book includes every child in the class.'}
              </p>

              <ul className="space-y-2.5 mb-5">
                {[
                  "Every student's messages",
                  'All photos & videos',
                  'Beautifully designed pages',
                  'Download instantly',
                  'Keep forever',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/85">
                    <Check size={16} weight="bold" className="text-amber-300 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* Loss aversion */}
              <div className="rounded-xl bg-rose-400/8 border border-rose-400/20 p-3.5 mb-5" data-testid="year6-pv-loss-aversion">
                <p className="text-rose-200 text-[13px] leading-relaxed">
                  💛 Don't lose these memories — once the year ends, this is everything they'll have left.
                </p>
              </div>

              <div className="border-t border-white/10 pt-5 mb-5">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/45 mb-1">One-time unlock</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-5xl font-black">£{data?.price_gbp || 99}</span>
                  <span className="text-white/55 text-sm">one-time</span>
                </div>
                <p className="text-white/55 text-sm">For the entire class (≈ £3 per child)</p>
                <p className="text-white/40 text-[12px] mt-1.5 italic" data-testid="year6-pv-price-microcopy">One small cost for a lifetime keepsake.</p>
              </div>

              {data?.memory_book_purchased ? (
                <div className="text-center">
                  {/* Updated-since-last-download badge — only when stats says version > local stamp */}
                  {stats && (
                    <UpdatedSinceLastDownloadBadge
                      eventId={eventId}
                      version={stats.memory_book_version}
                    />
                  )}
                  <p className="text-[12px] text-emerald-300 font-bold mb-3 inline-flex items-center gap-1.5">
                    <Check size={14} weight="bold" /> Already unlocked for this class
                  </p>
                  <a
                    href={`/year6/${slug}/success`}
                    onClick={() => stampDownloadedVersion(eventId, stats?.memory_book_version)}
                    className="block w-full text-center py-3.5 rounded-xl text-sm font-black text-white"
                    style={{ background: 'linear-gradient(135deg, #22C55E, #10B981)' }}
                    data-testid="year6-pv-already-unlocked"
                  >
                    Open Memory Book →
                  </a>
                </div>
              ) : (
                <>
                  {/* Almost-ready boost — only when NOT low_content and IS almost_ready */}
                  {stats && !stats.low_content && stats.almost_ready && (
                    <AlmostReadyBoost slug={slug} testIdSuffix="parent" />
                  )}

                  {/* End-of-term urgency — ONLY when content is in good shape (c-ii rule).
                      Hidden during very_low / low so we don't stack reassurance + urgency. */}
                  {stats && !stats.low_content && !stats.very_low_content && (
                    <div
                      className="rounded-xl bg-amber-400/10 border border-amber-400/25 p-3.5 mb-4 text-center"
                      data-testid="year6-pv-urgency"
                    >
                      <p className="text-amber-200 font-black text-[12.5px] uppercase tracking-[0.16em]">
                        Available until the end of term
                      </p>
                      <p className="text-white/75 text-[13px] leading-relaxed mt-1">
                        Don't miss these moments — they won't be captured again.
                      </p>
                    </div>
                  )}

                  {/* Preview the book — primary curiosity trigger BEFORE the paywall CTA */}
                  <button
                    onClick={() => setPreviewOpen(true)}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-[13.5px] font-bold text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] mb-3 transition-colors"
                    data-testid="year6-pv-preview-cta"
                  >
                    <BookOpen size={15} weight="fill" /> 👀 Preview Memory Book
                  </button>

                  <button
                    onClick={handleUnlock}
                    disabled={checkingOut}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-black text-white shadow-2xl disabled:opacity-50 hover:scale-[1.01] transition-transform"
                    style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                    data-testid="year6-pv-unlock-cta"
                  >
                    <LockKey size={18} weight="fill" />
                    {checkingOut
                      ? 'Preparing your Memory Book…'
                      : selectedChild
                        ? `Unlock ${(selectedChild.name || '').split(' ')[0]}'s Memory Book — £99`
                        : 'Unlock Their Memory Book — £99'}
                  </button>
                  <p className="text-center text-[12px] text-white/55 mt-2.5" data-testid="year6-pv-cta-microcopy">
                    Takes 30 seconds • Instant download
                  </p>
                  <p className="text-center text-[11.5px] text-amber-200/70 mt-2 italic" data-testid="year6-pv-cert-reassurance">
                    Includes {selectedChild ? `${(selectedChild.name || '').split(' ')[0]}'s` : 'your child\u2019s'} personalised certificate 🎓
                  </p>

                  {/* What happens next — trust booster */}
                  <div className="mt-5 rounded-xl bg-emerald-400/6 border border-emerald-400/18 p-4" data-testid="year6-pv-whats-next">
                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-300 mb-2">After payment</p>
                    <ul className="space-y-1.5">
                      {['Download instantly', 'Share with other parents', 'Keep forever'].map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-white/85">
                          <Check size={13} weight="bold" className="text-emerald-300 mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Secondary preview trigger — subtle curiosity below paywall */}
                  <button
                    onClick={() => setPreviewOpen(true)}
                    className="mt-4 w-full text-center text-[12px] text-white/55 hover:text-white/80 underline underline-offset-2 inline-flex items-center justify-center gap-1.5"
                    data-testid="year6-pv-preview-link"
                  >
                    <ImgIcon size={12} weight="fill" /> Preview a few pages
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Trust — parent-specific social proof */}
          <div className="text-center mt-8" data-testid="year6-pv-trust">
            <p className="text-white/65 text-sm font-semibold mb-1">💛 Parents love this — it becomes something they keep forever</p>
            <p className="text-white/40 text-xs">A memory your child will keep for life.</p>
          </div>
        </div>
      </section>

      <footer className="px-6 pt-6 pb-12 text-center text-white/30 text-xs">
        EventDrop · Year 6 Leavers Memory Experience 🎓
      </footer>

      <Year6BookPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        data={data}
        slug={slug}
        onUnlock={handleUnlock}
        unlocking={checkingOut}
        unlocked={!!data?.memory_book_purchased}
      />

      {/* Child Picker — frictionless ownership selector */}
      <AnimatePresence>
        {childPickerOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/72 backdrop-blur-sm p-0 sm:p-4"
            onClick={() => setChildPickerOpen(false)}
            data-testid="year6-pv-child-picker"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full sm:max-w-md bg-[#0F1014] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 text-white shadow-2xl"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200">Make it personal</p>
                  <h3 className="font-serif text-2xl font-black leading-tight">Who is your child?</h3>
                </div>
                <button onClick={() => setChildPickerOpen(false)} aria-label="Close" className="p-2 rounded-full hover:bg-white/8 text-white/60" data-testid="year6-pv-child-picker-close">
                  <X size={16} weight="bold" />
                </button>
              </div>
              <div className="relative mb-3">
                <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  autoFocus
                  type="text"
                  value={childQuery}
                  onChange={(e) => setChildQuery(e.target.value)}
                  placeholder="Search the class list…"
                  className="w-full pl-9 pr-3 py-3 rounded-xl bg-white/[0.05] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-amber-300/50"
                  data-testid="year6-pv-child-picker-input"
                />
              </div>
              <div className="max-h-[50vh] overflow-y-auto space-y-1">
                {filteredRoster.map((s) => (
                  <button
                    key={s.student_id}
                    onClick={() => persistChild(s)}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-white/8 text-white text-sm font-bold flex items-center justify-between gap-2"
                    data-testid={`year6-pv-child-option-${s.student_id}`}
                  >
                    <span>{s.name}</span>
                    {selectedChild?.student_id === s.student_id && <Check size={14} weight="bold" className="text-emerald-300" />}
                  </button>
                ))}
                {filteredRoster.length === 0 && (
                  <p className="text-white/40 text-xs italic px-3 py-3">No matches — try a different name</p>
                )}
              </div>
              <button
                onClick={() => persistChild(null)}
                className="w-full mt-4 text-center text-[12px] text-white/45 hover:text-white/75 underline underline-offset-2"
                data-testid="year6-pv-child-picker-skip"
              >
                Skip — show me the whole class
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-mono tabular-nums text-2xl sm:text-3xl font-black">{n ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/40 mt-0.5">{label}</div>
    </div>
  );
}
